<template>
    <div>
        
        <div class="caseload-chart-text">
            <div v-if="daysLeftInTheYear > 0" class="days-remaining">
                <p v-if="daysLeftInTheYear > 1">
                    There are <u class="number">{{daysLeftInTheYear}}</u> school days left in this year's observation window.
                </p>
                <p v-else="daysLeftInTheYear === 1">
                    There is <u class="number">{{daysLeftInTheYear}}</u> school day left in this year's observation window.
                </p>
            </div>
            <div v-else class="days-remaining">
                <p>The deadline to conduct observations has passed.</p>
            </div>
            <div v-if="getNumberOfObsToBeOnTrack(expectedObservations, numberOfSelectedObservations) > 0" class="on-track-text">
                <p>
                    To be <span>on track</span>, you should complete 
                    {{getNumberOfObsToBeOnTrack(expectedObservations, numberOfSelectedObservations)}} observations by the end of this month.
                    <br>
                </p>
            </div>
        </div>

        <div class="caseload-charts">

            <div class="flex">

                <div class="fall-window">
                    <p class="chart-title">Fall Window</p>
                    <CaseloadChart v-if="displayFallWindow"
                        class="caseload-chart fall"
                        :chartData="chartDataFall"
                        :width="400"
                        :height="250" />
                </div>

                <div class="spring-window">
                    <p class="chart-title" 
                        :style="hideSpringWindow === false ? 'marginLeft: 4rem' : null">
                        Spring Window
                    </p>
                    <CaseloadChart v-if="!hideSpringWindow"
                        class="caseload-chart spring"
                        :chartData="chartDataSpring"
                        :width="400"
                        :height="250" />
                    <div v-else class="caseload-grey spring"></div>    
                </div>

            </div>

            <div class="charts-legend flex">
                <div class="flex" v-for="(item, index) in legend" :key="index">
                    <div class="legend-color" :style="{backgroundColor: item.color}"></div>
                    <p>{{item.text}}</p>
                </div>
            </div>        

        </div>

    </div>
</template>

<script>
import Vue from 'vue'
import Component from 'vue-class-component'
import CaseloadChart from '@/helpers/CaseloadChart'
import format from 'date-fns/format'

@Component({
    components: {
        CaseloadChart
    },
    props: {
        selectedObservations: Array,
        completedObservations: Number,
        expectedObservations: Number,
        selectedSchool: Object,
        fiscalYear: Number
    },
    computed: {
        daysLeftInTheYear(){
            return this.selectedSchool.motp_data.find(s => s.FiscalYear === this.fiscalYear).ObservationDays_Remaining
        },
        abbreviateMonthsInTheYear(){
            let months = Object.keys(this.fiscalYearMonths)
            return months.map(m => `${m.substring(0,3)}.`)
        },
        numberOfSelectedObservations(){
            return this.selectedObservations.length
        },
        hideSpringWindow(){
            return this.currentDate >= this.firstDayOfNextFallTerm && this.currentDate < this.firstDayOfNextSpringTerm
        }
    }
})

export default class CaseloadChartContainer extends Vue {
    format = format
    currentMonthIndex = null
    currentDate =  Date.parse(new Date())    
    firstDayOfNextFallTerm =  Date.parse(new Date('2019, 09, 05'))
    firstDayOfNextSpringTerm =  Date.parse(new Date('2020, 01, 28'))
    displayFallWindow = true
    chartDataFall = null
    chartDataSpring = null
    fiscalYearMonths = {
        "September": 0,
        "October": 0,
        "November": 0,
        "December": 0,
        "January": 0,
        "February": 0,
        "March": 0,
        "April": 0,
        "May": 0,
        "June": 0
    }
    reportTargetPerMonth = [
       '7%', '15%', 
       '19%', '25%', 
       '42%', '53%',
       '72%', '82%',
       '91%', '100%'
    ]
    legend = [
        {
            text: 'Progress this month',
            color: '#613f75'
        },
        {
            text: 'Previous progress',
            color: '#d8d8d8'
        },
        {
            text: 'Suggested pacing line',
            color: '#367a31'
        },
    ]
    
    mounted(){
        this.fillData()
    }

    parseReportTargetPerMonth(reportTargetPerMonth){
        let arr = []
        reportTargetPerMonth.forEach(function(element) {
           return arr.push(parseFloat(element) / 100)
        })
        return arr
    }

    getCurrentMonthReportTarget(reportTargetPerMonth){
        let currentMonthIndex = this.currentMonthIndex - 1

        let currentMonthTarget = parseFloat(reportTargetPerMonth[currentMonthIndex]) / 100

        return currentMonthTarget
    }

    getNumberOfObsToBeOnTrack(expectedObservations, completedObservations){

        let currentMonthTarget = this.getCurrentMonthReportTarget(this.reportTargetPerMonth)

        let expectedObsToBeOnTrack = (currentMonthTarget * expectedObservations).toFixed()

        let remainingObsToBeOnTrack = expectedObsToBeOnTrack - completedObservations

        return remainingObsToBeOnTrack
    }

    getObservationsPerMonth(observations){

        let _observations = observations.map(s => s.MOTPMonth)

        let observationsPerMonth = _observations.reduce((accumulator, currentValue) => {
            if (currentValue in accumulator) {
                accumulator[currentValue]++;
            } else {
                accumulator[currentValue] = 1
            }
            return accumulator
        }, {});
        
        return observationsPerMonth
        
    }

    currentProgressPerFiscalYearMonth(fiscalYearMonths){

        let arr = []
        let _observations = this.getObservationsPerMonth(this.selectedObservations)

        for (let prop in fiscalYearMonths) {

            let obj = {}

            if (prop in _observations) {
                fiscalYearMonths[prop] = _observations[prop]
                obj[prop] = _observations[prop]
                arr.push(obj)
            } else {
                fiscalYearMonths[prop] = 0
                obj[prop] = 0
                arr.push(obj)
            }
            
        }

        return arr

    }

    getCumulativeProgress(){
        
        let currentMonth = format(new Date(), 'MMMM')
        let arr = []
        let propIndex = 0
        let currentMonthIndex = 0
        let cumulativeCount = 0
        
        let cumulativeCompletedObservations = this.currentProgressPerFiscalYearMonth(this.fiscalYearMonths).reduce((previousValue, currentValue) => {
            for (let prop2 in currentValue) {
                propIndex++
                    if (prop2 === currentMonth) {
                        currentMonthIndex = propIndex
                        this.currentMonthIndex = currentMonthIndex
                    }
                    if (currentMonthIndex === 0 || currentMonthIndex === propIndex){
                        cumulativeCount += currentValue[prop2]
                        previousValue = currentValue
                        previousValue[prop2] = cumulativeCount
                        arr.push(currentValue)
                    } else {
                        previousValue[prop2] = 0
                        arr.push(currentValue)
                    }
                }
            return previousValue
        }, {});
        return arr
    }

    previousProgressPerFiscalYearMonth(progressThisMonth){

        let endSlice = this.currentMonthIndex - 1

        let august = {
            'August': 0
        }

        let septemberToMayProgress = progressThisMonth.slice(0, endSlice)

        let arr = [august, ...septemberToMayProgress]

        return arr

    } 

    createChartValues(observationsArray){

        let arr = []

        observationsArray.reduce((previousValue, currentValue) => {
            for (let prop in currentValue) {
                previousValue = currentValue[prop]/this.expectedObservations
                arr.push(previousValue)
            }
            return currentValue
        }, {})

        return arr

    }

    getDataForFallWindow(f){
        return f.slice(0,5)
    }

    getDataForSpringWindow(s) {
        return s.slice(5,11)
    }

    fillData(){
        this.chartDataFall = {
            labels: this.getDataForFallWindow(this.abbreviateMonthsInTheYear),
            datasets: [
                {
                    label: 'Suggested pacing line',
                    backgroundColor: '#367a31',
                    data: this.getDataForFallWindow(this.parseReportTargetPerMonth(this.reportTargetPerMonth)),
                    borderWidth: 3.5, 
                    borderColor: '#367a31',
                    type: 'line',
                    fill: false
                },
                {
                    label: 'Previous progress',
                    backgroundColor: '#d8d8d8',
                    data: this.getDataForFallWindow(this.createChartValues(this.previousProgressPerFiscalYearMonth(this.getCumulativeProgress()))),
                    borderWidth: 1,
                    borderColor: '#d8d8d8'
                },
                {
                    label: 'Progress this month',
                    backgroundColor: '#613f75',
                    data: this.getDataForFallWindow(this.createChartValues(this.currentProgressPerFiscalYearMonth(this.fiscalYearMonths))),
                    borderWidth: 1,
                    borderColor: '#613f75'
                },
            ]
        }

        this.chartDataSpring = {
            labels: this.getDataForSpringWindow(this.abbreviateMonthsInTheYear),
            datasets: [
                {
                    label: 'Suggested pacing line',
                    backgroundColor: '#367a31',
                    data: this.getDataForSpringWindow(this.parseReportTargetPerMonth(this.reportTargetPerMonth)),
                    borderWidth: 3.5, 
                    borderColor: '#367a31',
                    type: 'line',
                    fill: false
                },
                {
                    label: 'Previous progress',
                    backgroundColor: '#d8d8d8',
                    data: this.getDataForSpringWindow(this.createChartValues(this.previousProgressPerFiscalYearMonth(this.getCumulativeProgress()))),
                    borderWidth: 1,
                    borderColor: '#d8d8d8'
                },
                {
                    label: 'Progress this month',
                    backgroundColor: '#613f75',
                    data: this.getDataForSpringWindow(this.createChartValues(this.currentProgressPerFiscalYearMonth(this.fiscalYearMonths))),
                    borderWidth: 1,
                    borderColor: '#613f75'
                },
            ]
        }
    }
    
}
</script>

<style lang="scss" scoped>
    div {
        .days-remaining {
            p {
                font-size: 25px;
                color: #4a4a4a;
                margin: 20px auto;
                text-align: center;
            }
        }
        .on-track-text {
            p {
                font-size: 18px;
                text-align: center;
                span {
                    color: #367a31;
                }
            }
        }
        .caseload-charts {
            margin-left: -50px;
            margin-top: 25px;
            .flex {
                justify-content: center;
            }
            .chart-title {
                text-align: center;
                font-size: 1.2rem; 
            }
            .fall-window {
                .chart-title {
                    margin-left: 5rem;
                }
            }
            .spring-window {
                margin-left: -15px;
                margin-bottom: 25px;
                .caseload-grey {
                    background-color:#f4f4f4; 
                    height: 211px;
                    width: 300px;
                    border: 1px solid #979797;
                    border-left: 0;
                    margin-top: 26px;
                    margin-bottom: 0;
                }
            }
            .charts-legend {
                max-width: 740px;
                margin: 1rem auto 0;
                justify-content: center;
                .flex {
                    align-items: center;
                    margin-right: 2rem;
                    &:first-child {
                        margin-left: 2rem;
                    }
                    .legend-color {
                        width: 40px;
                        height: 10px;
                        margin-right: 1rem;
                    }
                }
            }
            
        }
    }
</style>